import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Notifications as NotificationsIcon,
  Add as AddIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  PersonAdd as PersonAddIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';

const OfficerHome = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalLoans: 0,
    pendingLoans: 0,
    approvedLoans: 0,
    activeLoans: 0,
    rejectedLoans: 0,
    verifiedLoans: 0,
    totalLoanAmount: 0,
    pendingRequests: 0,
    closedLoans: 0,
  });
  const [recentLoans, setRecentLoans] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [user]);

  const fetchDashboardData = async () => {
    if (!user?.userId) return;
    
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [
        customersRes,
        loansRes,
        requestsRes,
        closedLoansRes,
      ] = await Promise.allSettled([
        api.get('/officer/customers', { params: { officerId: user.userId } }),
        api.get('/officer/loans', { params: { officerId: user.userId } }),
        api.get('/officer/customer-requests', { params: { officerId: user.userId } }),
        api.get('/officer/loans/closed-by-admin', { params: { officerId: user.userId } }).catch(() => ({ data: [] })),
      ]);

      const customers = customersRes.status === 'fulfilled' ? customersRes.value.data : [];
      const loans = loansRes.status === 'fulfilled' ? loansRes.value.data : [];
      const requests = requestsRes.status === 'fulfilled' ? requestsRes.value.data : [];
      const closedLoans = closedLoansRes.status === 'fulfilled' ? closedLoansRes.value.data : [];

      // Calculate statistics
      const totalCustomers = customers.length;
      const totalLoans = loans.length;
      const pendingLoans = loans.filter(l => l.status === 'PENDING').length;
      const approvedLoans = loans.filter(l => l.status === 'APPROVED').length;
      const activeLoans = loans.filter(l => l.status === 'ACTIVE').length;
      const rejectedLoans = loans.filter(l => l.status === 'REJECTED').length;
      const verifiedLoans = loans.filter(l => l.status === 'VERIFIED').length;
      const totalLoanAmount = loans
        .filter(l => l.principalAmount)
        .reduce((sum, l) => sum + parseFloat(l.principalAmount || 0), 0);
      const pendingRequests = requests.filter(r => r.status === 'PENDING').length;
      const closedLoansCount = closedLoans.length;

      setStats({
        totalCustomers,
        totalLoans,
        pendingLoans,
        approvedLoans,
        activeLoans,
        rejectedLoans,
        verifiedLoans,
        totalLoanAmount,
        pendingRequests,
        closedLoans: closedLoansCount,
      });

      // Get recent loans (last 5, sorted by updated date)
      const sortedLoans = [...loans]
        .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
        .slice(0, 5);
      setRecentLoans(sortedLoans);

      // Get recent customers (last 5, sorted by created date)
      const sortedCustomers = [...customers]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      setRecentCustomers(sortedCustomers);

      // Calculate notification count
      const viewedNotifications = JSON.parse(localStorage.getItem(`viewedNotifications_${user.userId}`) || '[]');
      const recentLoansCount = loans.filter(loan => {
        const isRecent = (loan.status === 'ACTIVE' || loan.status === 'APPROVED' || loan.status === 'REJECTED') &&
          loan.updatedAt && 
          new Date(loan.updatedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const isViewed = viewedNotifications.includes(`loan-${loan.id}`);
        return isRecent && !isViewed;
      }).length;
      
      const recentRequestsCount = requests.filter(request => {
        const isRecent = (request.status === 'APPROVED' || request.status === 'REJECTED' || request.status === 'APPROVED_FOR_EDIT') &&
          request.reviewedAt &&
          new Date(request.reviewedAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        const isViewed = viewedNotifications.includes(`request-${request.id}`);
        return isRecent && !isViewed;
      }).length;

      setNotificationCount(recentLoansCount + recentRequestsCount);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'APPROVED':
        return 'info';
      case 'PENDING':
      case 'VERIFIED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'APPROVED':
        return 'Approved';
      case 'PENDING':
        return 'Pending';
      case 'VERIFIED':
        return 'Verified';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-LK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const StatCard = ({ title, value, icon, color, gradient, onClick, subtitle }) => (
    <Card
      sx={{
        background: gradient || `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
        color: 'white',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: 3,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const approvalRate = stats.totalLoans > 0 
    ? ((stats.approvedLoans + stats.activeLoans) / stats.totalLoans * 100).toFixed(1)
    : 0;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#0D47A1',
            mb: 1,
            fontSize: { xs: '1.75rem', sm: '2.25rem' },
          }}
        >
          Welcome back, {user?.fullName || 'Loan Officer'}!
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Here's an overview of your loan management activities
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Customers"
            value={stats.totalCustomers}
            icon={<PeopleIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)"
            onClick={() => navigate('/officer/customers')}
            subtitle="Registered customers"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Loans"
            value={stats.totalLoans}
            icon={<DescriptionIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)"
            onClick={() => navigate('/officer/my-loans')}
            subtitle={`${stats.activeLoans} active`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Approval"
            value={stats.pendingLoans + stats.verifiedLoans}
            icon={<PendingIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
            onClick={() => navigate('/officer/loans/pending-verified')}
            subtitle="Awaiting admin review"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Loan Amount"
            value={formatCurrency(stats.totalLoanAmount)}
            icon={<AccountBalanceIcon sx={{ fontSize: 32 }} />}
            gradient="linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)"
            subtitle="All time"
          />
        </Grid>
      </Grid>

      {/* Loan Status Sections */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#0D47A1' }}>
        Loan Applications
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => navigate('/officer/loans/active')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)',
                borderColor: 'success.main',
              },
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.activeLoans}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Active Loans
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => navigate('/officer/loans/rejected')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)',
                borderColor: 'error.main',
              },
            }}
          >
            <WarningIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.rejectedLoans}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Rejected Loans
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => navigate('/officer/loans/closed')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)',
                borderColor: 'info.main',
              },
            }}
          >
            <AssessmentIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.closedLoans}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Closed Loans
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => navigate('/officer/loans/pending-verified')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)',
                borderColor: 'warning.main',
              },
            }}
          >
            <PendingIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {stats.pendingLoans + stats.verifiedLoans}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pending / Verified Loans
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Secondary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {approvalRate}%
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Approval Rate
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => navigate('/officer/notifications')}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 4,
                transform: 'translateY(-4px)',
              },
            }}
          >
            <NotificationsIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {notificationCount}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              New Notifications
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#0D47A1' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => navigate('/officer/customers/register')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565C0 0%, #0A3D7A 100%)',
                },
              }}
            >
              Register Customer
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/officer/loans')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1B5E20 0%, #0D3E0F 100%)',
                },
              }}
            >
              New Loan Application
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/officer/my-loans')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                },
              }}
            >
              View My Loans
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<NotificationsIcon />}
              onClick={() => navigate('/officer/notifications')}
              sx={{
                py: 1.5,
                background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7B1FA2 0%, #6A1B9A 100%)',
                },
              }}
            >
              View Notifications
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        {/* Recent Loans */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Recent Loan Applications
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/officer/my-loans')}
              >
                View All
              </Button>
            </Box>
            {recentLoans.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No loan applications yet
                </Typography>
              </Box>
            ) : (
              <List>
                {recentLoans.map((loan, index) => (
                  <React.Fragment key={loan.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: theme.palette[getStatusColor(loan.status)]?.main || 'grey.500',
                          }}
                        >
                          <DescriptionIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {loan.customer?.fullName || 'Unknown Customer'}
                            </Typography>
                            <Chip
                              label={getStatusLabel(loan.status)}
                              color={getStatusColor(loan.status)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Amount: {formatCurrency(loan.principalAmount || 0)} • {formatDate(loan.updatedAt || loan.createdAt)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentLoans.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Recent Customers */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D47A1' }}>
                Recent Customers
              </Typography>
              <Button
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/officer/customers')}
              >
                View All
              </Button>
            </Box>
            {recentCustomers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  No customers registered yet
                </Typography>
              </Box>
            ) : (
              <List>
                {recentCustomers.map((customer, index) => (
                  <React.Fragment key={customer.id}>
                    <ListItem
                      sx={{
                        px: 0,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <PeopleIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {customer.fullName}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              NIC: {customer.nic} • {formatDate(customer.createdAt)}
                            </Typography>
                            {customer.area && (
                              <Typography variant="caption" color="textSecondary">
                                Area: {customer.area.name}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentCustomers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Pending Requests Alert */}
      {stats.pendingRequests > 0 && (
        <Paper
          sx={{
            p: 2.5,
            mt: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
            border: '1px solid',
            borderColor: 'warning.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningIcon sx={{ color: 'warning.main', fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {stats.pendingRequests} Pending Customer Request{stats.pendingRequests > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                You have customer update requests waiting for admin approval
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="warning"
              onClick={() => navigate('/officer/customers')}
            >
              View Requests
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default OfficerHome;
